<template>
	<!--
	Use `key` to re-render the popover when the loading state changes to
	avoid the horizontal flicker bug. This closes and re-opens the dropdown.
	Not ideal, but seems to be good enough to avoid the flicker.
	-->
	<VMenu
		v-bind="popoverProps"
		:key="key"
		:triggers="[]"
		:hide-triggers="['click']"
		:popper-hide-triggers="[]"
		:delay="{ hide: 0 }"
		:distance="distance"
		:overflow-padding="24"
		:shown="isOpen"
		class="app-dropdown"
		:class="classAddons"
		@auto-hide="handleHide"
		@hide="$emit('hide', $event)"
		@apply-show="$emit('apply-show', $event)"
		@apply-hide="handleApplyHide"
	>
		<button
			v-if="!popoverProps['disabled']"
			v-tooltip="triggerButtonProps && triggerButtonProps['v-tooltip']"
			v-bind="triggerButtonProps"
			type="button"
			class="app-dropdown__trigger"
			@click="handleDropdownToggled"
		>
			<slot :is-open="isOpen" name="trigger" />
		</button>
		<slot v-else :is-open="isOpen" name="trigger" />

		<template #popper>
			<slot :close-dropdown="handleHide" :is-open="isOpen" />
		</template>
	</VMenu>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';

/**
 * This component encapsulates dropdowns and solves a few annoying bugs related
 * to the dropdowns and loading data into dropdowns.
 *
 * Use this for:
 * 1. Deferred opening of the dropdown - do not open right away when the user
 *    opens the dropdown, but open it when the data has loaded (or the request
 *    has already loaded for a few ms). The dropdownt will only open once
 *    `isLoading` or `hasContent` have been set to `true`, NOT when the user
 *    clicks on the trigger button.
 * 2. Avoiding a bug where the dropdown flickers horizontally when the dropdown
 *    content width changes. This can happen with loading states, if a dropdown
 *    has a loading state that is narrower than the content, then the dropdown
 *    will flicker. This bug comes from the v-tooltip library.
 *
 * @example
 * <AppDropdown
 *   :is-loading="loading"
 *   :has-content="loading || !!result"
 * >
 *   <template #trigger><SuiIcon name="icon-bookmark-24" /></template>
 *
 *   <Loader v-if="loading" />
 *   <Content v-else-if="result">...</Content>
 * </AppDropdown>
 */

export type AppDropdownProps = {
	isLoading?: boolean;
	hasContent?: boolean;
	triggerButtonProps?: Record<string, unknown>;
	popoverProps?: Record<string, unknown>;
	isFullWidth?: boolean;
	isFullHeight?: boolean;
	isInitiallyOpen?: boolean;
};

const props = withDefaults(defineProps<AppDropdownProps>(), {
	isLoading: false,
	hasContent: true,
	triggerButtonProps: () => ({}),
	popoverProps: () => ({}),
	isFullWidth: false,
	isFullHeight: false,
	isInitiallyOpen: false,
});

const emit = defineEmits(['hide', 'apply-show', 'show']);

const dropdownOpen = ref(props.isInitiallyOpen);
const shouldApplyHide = ref(true);
const key = computed(() => (props.isLoading ? 'loading' : 'content'));

const handleDropdownToggled = (e: MouseEvent) => {
	if (props.triggerButtonProps.preventEventPropagation) {
		e.stopPropagation();
	}

	dropdownOpen.value = !dropdownOpen.value;

	if (dropdownOpen.value) {
		emit('show');
	}
};

const isOpen = computed(() => {
	return dropdownOpen.value && props.hasContent;
});

const distance = computed(() => {
	return props.popoverProps.distance ?? 18;
});

const handleHide = () => {
	dropdownOpen.value = false;
};

watch(key, () => {
	shouldApplyHide.value = false;

	nextTick(() => {
		shouldApplyHide.value = true;
	});
});

const handleApplyHide = () => {
	if (shouldApplyHide.value) {
		handleHide();
	}
};

const classAddons = computed(() => {
	return {
		'app-dropdown--full-width': props.isFullWidth,
		'app-dropdown--full-height': props.isFullHeight,
	};
});

defineExpose({
	handleApplyHide,
});
</script>

<style lang="scss">
.app-dropdown {
	line-height: 0;

	&__trigger {
		line-height: 0;
	}

	&--full-width {
		.app-dropdown__trigger {
			width: 100%;
		}
	}

	&--full-height {
		.app-dropdown__trigger {
			height: 100%;
		}
	}
}
</style>
